import { useMemo, useState } from "react";
import { Check, Clock } from "lucide-react";

import { playbookCategories } from "@/data/playbooks/categories";
import { getAllPlaybooks, getPlaybooksByCategory } from "@/data/playbooks/registry";
import { MockGenieConfig } from "@/types/mockups/genie-setup-v2";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

interface Step3PlaybooksProps {
  config: MockGenieConfig;
  onChange: (config: MockGenieConfig) => void;
}

export const Step3Playbooks = ({ config, onChange }: Step3PlaybooksProps) => {
  const [showAll, setShowAll] = useState(false);

  const playbooks = useMemo(() => {
    if (showAll || !config.job.categoryId) return getAllPlaybooks();
    return getPlaybooksByCategory(config.job.categoryId);
  }, [showAll, config.job.categoryId]);

  const selectedCategory = config.job.categoryId
    ? playbookCategories.find((c) => c.id === config.job.categoryId)
    : null;

  const togglePlaybook = (playbookId: string) => {
    const exists = config.playbookIds.includes(playbookId);
    const next = exists
      ? config.playbookIds.filter((id) => id !== playbookId)
      : [...config.playbookIds, playbookId];
    onChange({ ...config, playbookIds: next });
  };

  return (
    <div className="space-y-5">
      {selectedCategory && !showAll && (
        <div className="bg-genie-primary/5 border border-genie-primary/20 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <GenieIcon name="clipboard-check" size={32} className="h-8 w-8 shrink-0" />
            <div>
              <p className="text-sm font-medium text-gray-900">
                Showing plays for: {selectedCategory.name}
              </p>
              <p className="text-xs text-gray-500">
                These fit your chosen job. Pick as many as you want.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setShowAll(true)}
            className="text-xs font-medium text-genie-primary hover:underline self-start sm:self-auto"
          >
            Show all plays
          </button>
        </div>
      )}

      {showAll && selectedCategory && (
        <div className="flex items-center justify-end">
          <button
            type="button"
            onClick={() => setShowAll(false)}
            className="text-xs font-medium text-genie-primary hover:underline"
          >
            ← Back to {selectedCategory.name} plays
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {playbooks.map((playbook) => {
          const isSelected = config.playbookIds.includes(playbook.meta.id);

          const inner = (
            <div className={`relative ${isSelected ? "rounded-[14px] bg-white" : "rounded-2xl"} p-5`}>
              {isSelected && (
                <div className="absolute top-4 right-4 w-6 h-6 rounded-full bg-genie-primary flex items-center justify-center">
                  <Check className="w-3.5 h-3.5 text-white" />
                </div>
              )}
              <div className="pr-8">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <h3 className="text-base font-semibold text-gray-900">{playbook.meta.name}</h3>
                  {playbook.meta.isFeatured && (
                    <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-genie-secondary/10 text-genie-secondary">
                      Popular
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-600 mb-3">{playbook.meta.tagline}</p>
                {playbook.meta.estimatedSetupTime && (
                  <div className="flex items-center gap-1 text-xs text-gray-500">
                    <Clock className="w-3 h-3" />
                    {playbook.meta.estimatedSetupTime}
                  </div>
                )}
              </div>
            </div>
          );

          if (isSelected) {
            return (
              <button
                key={playbook.meta.id}
                type="button"
                onClick={() => togglePlaybook(playbook.meta.id)}
                className="text-left p-[2px] rounded-2xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] transition-all"
              >
                {inner}
              </button>
            );
          }

          return (
            <button
              key={playbook.meta.id}
              type="button"
              onClick={() => togglePlaybook(playbook.meta.id)}
              className="text-left rounded-2xl bg-white border-2 border-gray-200 hover:border-genie-primary/40 hover:shadow-md transition-all"
            >
              {inner}
            </button>
          );
        })}
      </div>

      {playbooks.length === 0 && (
        <div className="text-center py-12 text-gray-500 text-sm">
          No plays available for this category yet.
        </div>
      )}
    </div>
  );
};
